import { Check, Close } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { Chip, IconButton } from '@mui/material';
import { useIsBottomFromTenantAdmin } from 'shared/permissions/useIsBottomFromTenantAdmin';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import DefaultLocationDialogForm from 'features/inventory/warehouses/components/form/DefaultLocationDialogForm';
import MiscDialogForm from 'features/inventory/warehouses/components/form/MiscProductWarehouseForm';
import DialogSourceCost from 'features/inventory/warehouses/cost_analysis/DialogSourceCost';
import { useState } from 'react';
import { getCodeLabel } from 'components/cells';

const stopEventPropagation = (event: React.MouseEvent) => {
  event.stopPropagation();
};

export const AssociationCell = ({ row }: { row: any }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Chip
        sx={{ background: 'transparent', cursor: 'pointer' }}
        icon={row.association ? <Check color="success" /> : <Close color="error" />}
        onClick={(e) => {
          stopEventPropagation(e);
          handleOpen();
        }}
        onMouseDown={stopEventPropagation}
      />
      {open && (
        <IsTenantAdministrator>
          <DialogSourceCost
            open={open}
            onClose={handleClose}
            productWarehouseId={row.id}
            productCode={getCodeLabel(row.product)}
            warehouseCode={getCodeLabel(row.warehouse)}
          />
        </IsTenantAdministrator>
      )}
    </>
  );
};

type ThresholdActionProps = {
  row: any;
  t: (key: string) => string;
};

type ManagePricesActionProps = ThresholdActionProps & {
  onManagePrices?: (product: string) => void;
};

type EditThresholdActionProps = ThresholdActionProps & {
  refresh?: any;
};

export const ManagePricesAction = ({ row, t, onManagePrices }: ManagePricesActionProps) => {
  if (!onManagePrices) return null;

  return (
    <IconButton size="small" onClick={() => onManagePrices(row.product)}>
      <PriceChangeIcon fontSize="small" /> {t('actions.manage_prices')}
    </IconButton>
  );
};

export const SourceCostAction = ({ row, t, onManagePrices }: ManagePricesActionProps) => {
  const [sourceCostOpen, setSourceCostOpen] = useState(false);
  if (!onManagePrices) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          stopEventPropagation(e);
          setSourceCostOpen(true);
        }}
        onMouseDown={stopEventPropagation}>
        <MonetizationOnIcon fontSize="small" /> {t('global.costs')}
      </IconButton>
      {sourceCostOpen && (
        <IsTenantAdministrator>
          <DialogSourceCost
            open={sourceCostOpen}
            onClose={() => setSourceCostOpen(false)}
            productWarehouseId={row.id}
            productCode={getCodeLabel(row.product)}
            warehouseCode={getCodeLabel(row.warehouse)}
          />
        </IsTenantAdministrator>
      )}
    </>
  );
};

export const EditThresholdAction = ({ row, t, refresh }: EditThresholdActionProps) => {
  const [editOpen, setEditOpen] = useState(false);
  if (!useIsBottomFromTenantAdmin()) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          stopEventPropagation(e);
          setEditOpen(true);
        }}
        onMouseDown={stopEventPropagation}>
        <EditIcon fontSize="small" /> {t('actions.edit')}
      </IconButton>
      {editOpen && (
        <MiscDialogForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          row={row}
          refresh={refresh}
        />
      )}
    </>
  );
};

export const DefaultLocationAction = ({ row, t, refresh }: EditThresholdActionProps) => {
  const [open, setOpen] = useState(false);
  if (!useIsBottomFromTenantAdmin()) return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          stopEventPropagation(e);
          setOpen(true);
        }}
        onMouseDown={stopEventPropagation}>
        <PinDropIcon fontSize="small" /> {t('actions.configure-default-location')}
      </IconButton>
      {open && (
        <DefaultLocationDialogForm
          open={open}
          onClose={() => setOpen(false)}
          row={row}
          refresh={refresh}
        />
      )}
    </>
  );
};
